import { computed, onScopeDispose, ref, watch, type ComputedRef, type Ref } from 'vue';
import type { ApiError } from '@/api/client';
import { asApiError } from '@/api/errors';
import { playsForMoney } from '@/api/grid';
import { pollIntervalFor } from '@/api/polling';
import type { GameDetail, Player, Square } from '@/api/types';
import { useGamePolling, type UseGamePolling } from '@/composables/useGamePolling';
import { useGamesStore } from '@/stores/games';
import { t } from '@/i18n';
import { useSessionStore } from '@/stores/session';

/**
 * Everything the three game screens need before they differ.
 *
 * `GameView`, `HostGameView` and `LocalPlayView` were written by copy-and-adapt and never
 * converged: nine identical computeds each, the same `load`, the same polling feed, the same
 * `watching`, and the same eight-line comment about router reuse written out three times. What
 * actually differs between them is one thing — the sentence a failure produces — so that is the
 * one thing passed in.
 *
 * @param code A getter, not a value. The route parameter changes under a component the router is
 *   reusing, and the watch below is what makes that work.
 */
export interface UseGameScreenOptions {
  /**
   * Which sentence a failure becomes.
   *
   * The genuinely per-screen part, and the reason this is a parameter rather than a branch: the
   * play screen names the squares somebody else took, the host screen distinguishes a second tab
   * that already locked from one that already scored, and local play talks about the person whose
   * turn it is. Each is right for its screen and wrong for the other two.
   *
   * It receives an `ApiError` — never a raw `catch` binding — so no screen has to defend against
   * a `TypeError: Failed to fetch`.
   *
   * **`null` means say nothing.** Local play needs that: a put-back of a square that was already
   * off the grid is exactly the outcome the tap asked for, so reporting it would name a failure
   * the room never suffered.
   */
  sentenceFor: (error: ApiError) => string | null;
}

export interface UseGameScreen {
  games: ReturnType<typeof useGamesStore>;
  session: ReturnType<typeof useSessionStore>;
  /** The last complaint, or null. Rendered by every screen; cleared by `run`. */
  message: Ref<string | null>;
  detail: ComputedRef<GameDetail | null>;
  /** `GameDetail['game']`, not `GameSummary` — the detail shape carries the colours, the
   * prices and the result, which the list shape does not. */
  game: ComputedRef<GameDetail['game'] | null>;
  isOpen: ComputedRef<boolean>;
  isLocked: ComputedRef<boolean>;
  isCancelled: ComputedRef<boolean>;
  isHost: ComputedRef<boolean>;
  players: ComputedRef<Player[]>;
  squares: ComputedRef<Square[]>;
  /** Whether this pool has money in it at all — see `playsForMoney`. */
  forMoney: ComputedRef<boolean>;
  /** Who this browser is, whether or not they have joined this game. */
  mySubjectId: ComputedRef<string | null>;
  load: () => Promise<void>;
  feed: UseGamePolling;
  /** Whether anything can still change — also whether a Refresh button would do anything. */
  watching: ComputedRef<boolean>;
  /** What the last poll changed, in words, for a live region. Null until something does. */
  boardNews: Ref<string | null>;
  handleFailure: (caught: unknown) => void;
  run: (action: () => Promise<unknown>, reloadOnSuccess?: boolean) => Promise<boolean>;
}

export function useGameScreen(code: () => string, options: UseGameScreenOptions): UseGameScreen {
  const games = useGamesStore();
  const session = useSessionStore();

  const message = ref<string | null>(null);

  const detail = computed(() => games.current);
  const game = computed(() => detail.value?.game ?? null);
  const isOpen = computed(() => game.value?.status === 'OPEN');
  const isLocked = computed(() => game.value?.status === 'LOCKED');
  const isCancelled = computed(() => game.value?.status === 'CANCELLED');
  const isHost = computed(() => detail.value?.you?.isHost ?? false);
  const players = computed(() => detail.value?.players ?? []);
  const squares = computed(() => detail.value?.squares ?? []);
  const forMoney = computed(() => playsForMoney(game.value));

  /*
   * The session id as a fallback, not just the game's view of the caller.
   *
   * `HostGameView` had only `detail.you.subjectId`, which is enough on a screen you reach by
   * being the host. The play screen needs to know who this browser is *before* they have joined,
   * to render their own squares as theirs — and the fuller answer is correct on all three, because
   * `you.subjectId` is populated from the session whenever there is one.
   */
  const mySubjectId = computed(() => detail.value?.you?.subjectId ?? session.subject?.id ?? null);

  const load = (): Promise<void> => games.load(code());

  /*
   * Watched, not mounted.
   *
   * Vue Router reuses this component when only the parameters change, so `/game/AAAAAA` →
   * `/game/BBBBBB` is the same instance with a new `code` and no second `onMounted` — and with the
   * load bound to mount alone, the screen went on showing the first game under the second game's
   * URL indefinitely. `immediate` is what keeps the first load, so this replaces `onMounted`
   * rather than joining it.
   */
  watch(code, load, { immediate: true });

  /*
   * The grid moves while people look at it, so it re-reads itself. Polling rather than a socket —
   * docs/decisions/0009-polling-not-websockets.md has the arithmetic — and it stops on its own
   * once the game is history, while the tab is hidden, or while this screen is mid-write.
   */
  const feed = useGamePolling({
    tick: () => games.refresh(code()),
    interval: () => pollIntervalFor(game.value?.status),
    busy: () => games.saving,
  });

  const watching = computed(() => pollIntervalFor(game.value?.status) !== null);

  /*
   * What a poll changed, for somebody who cannot see the board change.
   *
   * A claim of your own is announced, and the idle banner is a `role="status"` — but when the grid
   * moved under a screen-reader user, three squares gone while they were deciding, nothing said
   * so. Announcing every poll would be noise, so this is the count and it waits for the burst to
   * settle: `QUIET_MS` after the last change, one sentence for however many squares went.
   *
   * Read off `squaresClaimed` rather than the squares array, because that is the number the API
   * keeps and the one that moves for a reason. Only *upwards*: a square coming back off the grid
   * is the host's doing, and the host is looking at it.
   */
  const boardNews = ref<string | null>(null);
  const QUIET_MS = 3000;
  let sinceLastSaid = 0;
  let quiet: ReturnType<typeof setTimeout> | undefined;

  watch(
    () => game.value?.squaresClaimed ?? 0,
    (now, before) => {
      if (now <= before) return;
      sinceLastSaid += now - before;

      if (quiet !== undefined) clearTimeout(quiet);
      quiet = setTimeout(() => {
        boardNews.value = t('game.moreTaken', { n: sinceLastSaid }, sinceLastSaid);
        sinceLastSaid = 0;
      }, QUIET_MS);
    },
  );

  onScopeDispose(() => {
    if (quiet !== undefined) clearTimeout(quiet);
  });

  function handleFailure(caught: unknown): void {
    message.value = options.sentenceFor(asApiError(caught));
  }

  /**
   * Runs a write, then re-reads the game — the screen and the server must not disagree.
   *
   * `reloadOnSuccess` exists for lock alone: it answers with the whole `GameDetail`, which the
   * store keeps, and under DISTRIBUTE_FREE that response is the only place the freshly dealt
   * squares appear without another round trip. Re-reading would throw away the authoritative
   * answer and pay for a second request to get it back.
   *
   * A *failure* always re-reads, whatever the action: something moved underneath this screen.
   */
  async function run(action: () => Promise<unknown>, reloadOnSuccess = true): Promise<boolean> {
    if (games.saving) return false;
    message.value = null;

    try {
      await action();
      if (reloadOnSuccess) await load();
      return true;
    } catch (caught) {
      handleFailure(caught);
      await load();
      return false;
    }
  }

  return {
    games,
    session,
    message,
    detail,
    game,
    isOpen,
    isLocked,
    isCancelled,
    isHost,
    players,
    squares,
    forMoney,
    mySubjectId,
    load,
    feed,
    watching,
    boardNews,
    handleFailure,
    run,
  };
}
